import React from "react";
import { AlertTriangleIcon, BatteryChargingIcon, BellIcon, BookOpenIcon, BuildingIcon, CheckIcon, ClockIcon, CompassIcon, CpuIcon, FactoryIcon, FuelIcon, GaugeIcon, GraduationCapIcon, HomeIcon, LandmarkIcon, MapIcon, MapPinIcon, MegaphoneIcon, PlugZapIcon, PowerOffIcon, SunIcon, UsersIcon, ZapIcon, BoxIcon } from "lucide-react";
import { POWER_SOURCE_LABEL, POWER_STATE_LABEL } from "../../data/power";
import { CONFIDENCE_LABEL, confirmedLabel } from "../../utils/power";
import { PowerConfidence, PowerLocationKind, PowerSourceKind, PowerState } from "../../types/power";
/**
 * One icon mapping for the whole Power experience, asked for by meaning rather
 * than by glyph. Nothing here is an emoji: status has to survive greyscale, a
 * screen reader and a 14px row, which a coloured pictograph does not.
 */
export const powerIcons = {
  power: ZapIcon,
  location: MapPinIcon,
  campus: LandmarkIcon,
  accommodation: HomeIcon,
  building: BuildingIcon,
  class: GraduationCapIcon,
  library: BookOpenIcon,
  studentCentre: UsersIcon,
  map: MapIcon,
  report: MegaphoneIcon,
  notification: BellIcon,
  grid: FactoryIcon,
  generator: FuelIcon,
  solar: SunIcon,
  inverter: PlugZapIcon,
  battery: BatteryChargingIcon,
  other: BoxIcon,
  unknown: BoxIcon,
  confidence: GaugeIcon,
  clock: ClockIcon,
  navigate: CompassIcon,
  facility: CpuIcon,
  ok: CheckIcon,
  warn: AlertTriangleIcon,
  off: PowerOffIcon
} as const;
export type PowerIconName = keyof typeof powerIcons;
export const LOCATION_ICON: Record<PowerLocationKind, PowerIconName> = {
  accommodation: 'accommodation',
  campus: 'campus',
  academic: 'class',
  library: 'library',
  facility: 'facility',
  social: 'studentCentre',
  residence: 'building'
};
export const SOURCE_ICON: Record<PowerSourceKind, PowerIconName> = {
  grid: 'grid',
  generator: 'generator',
  solar: 'solar',
  inverter: 'inverter',
  other: 'other',
  unknown: 'unknown'
};

/**
 * Status treatment. Every state pairs a colour with a distinct icon and its own
 * word, so it reads correctly in greyscale and to a screen reader.
 */
const STATE_STYLE: Record<PowerState, {
  pill: string;
  tile: string;
  dot: string;
  icon: PowerIconName;
  marker: string;
}> = {
  on: {
    pill: 'border-ok-100 bg-ok-50 text-ok-800',
    tile: 'bg-ok-50 text-ok-800',
    dot: 'bg-ok-700',
    icon: 'power',
    marker: '#16794a'
  },
  off: {
    pill: 'border-danger-100 bg-danger-50 text-danger-800',
    tile: 'bg-danger-50 text-danger-800',
    dot: 'bg-danger-700',
    icon: 'off',
    marker: '#b42318'
  },
  unstable: {
    pill: 'border-warn-100 bg-warn-50 text-warn-800',
    tile: 'bg-warn-50 text-warn-800',
    dot: 'bg-warn-700',
    icon: 'warn',
    marker: '#b54708'
  },
  backup: {
    pill: 'border-info-100 bg-info-50 text-info-800',
    tile: 'bg-info-50 text-info-800',
    dot: 'bg-info-700',
    icon: 'battery',
    marker: '#175cd3'
  },
  unknown: {
    pill: 'border-line bg-canvas text-ink-muted',
    tile: 'bg-canvas text-ink-muted',
    dot: 'bg-ink-subtle',
    icon: 'unknown',
    marker: '#66748c'
  }
};
export function stateMarkerColour(state: PowerState): string {
  return STATE_STYLE[state].marker;
}

/** The status pill used everywhere a state appears. */
export function PowerStatusPill({
  state,
  size = 'sm'
}: {
  state: PowerState;
  size?: 'xs' | 'sm';
}) {
  const style = STATE_STYLE[state];
  const Icon = powerIcons[style.icon];
  return <span className={`inline-flex shrink-0 items-center gap-1 rounded-full border font-semibold uppercase tracking-normal ${style.pill} ${size === 'xs' ? 'px-2 py-0.5 text-caption' : 'px-2.5 py-1 text-caption'}`} data-id="element-4335" data-mp="123yj5udz3d91">
      <Icon className="h-3.5 w-3.5" aria-hidden data-id="element-4336" data-mp="1bzjj7mzhe3g5" />
      {POWER_STATE_LABEL[state]}
      <span className="sr-only" data-id="element-4337" data-mp="170yjdoo55ls8">power status</span>
    </span>;
}

/** The soft icon container the design uses in place of a bare icon beside text. */
export function IconTile({
  name,
  tone = 'neutral',
  size = 'md'
}: {
  name: PowerIconName;
  tone?: 'neutral' | 'brand' | 'ok' | 'warn' | 'danger' | 'info';
  size?: 'sm' | 'md' | 'lg';
}) {
  const Icon = powerIcons[name];
  const tones: Record<string, string> = {
    neutral: 'bg-canvas text-ink-muted',
    brand: 'bg-brand-50 text-brand-700',
    ok: 'bg-ok-50 text-ok-800',
    warn: 'bg-warn-50 text-warn-800',
    danger: 'bg-danger-50 text-danger-800',
    info: 'bg-info-50 text-info-800'
  };
  const sizes = {
    sm: 'h-8 w-8 rounded-lg',
    md: 'h-10 w-10 rounded-xl',
    lg: 'h-12 w-12 rounded-xl'
  };
  const glyph = {
    sm: 'h-4 w-4',
    md: 'h-[1.15rem] w-[1.15rem]',
    lg: 'h-6 w-6'
  };
  return <span className={`flex shrink-0 items-center justify-center border border-line/60 ${tones[tone]} ${sizes[size]}`} aria-hidden data-id="element-4338" data-mp="1kqbcj23732te">
      <Icon className={glyph[size]} data-id="element-4339" data-mp="1oq277gvue337" />
    </span>;
}
export function stateTile(state: PowerState): string {
  return STATE_STYLE[state].tile;
}

/** Freshness, phrased rather than colour-coded. */
export function FreshnessLine({
  at
}: {
  at: string | null;
}) {
  const Icon = powerIcons.clock;
  return <span className="inline-flex items-center gap-1 text-caption text-ink-subtle" data-id="element-4340" data-mp="28u9gs9q25v61">
      <Icon className="h-3 w-3" aria-hidden data-id="element-4341" data-mp="23sag57lpdccs" />
      {confirmedLabel(at)}
    </span>;
}
export function ConfidenceLine({
  confidence
}: {
  confidence: PowerConfidence;
}) {
  const Icon = powerIcons.confidence;
  return <span className="inline-flex items-center gap-1 text-caption text-ink-subtle" data-id="element-4342" data-mp="1uv9v9axudwe3">
      <Icon className="h-3 w-3" aria-hidden data-id="element-4343" data-mp="2riy39046w8jx" />
      {CONFIDENCE_LABEL[confidence]}
    </span>;
}
export function sourceLabel(source: PowerSourceKind): string {
  return POWER_SOURCE_LABEL[source];
}

/** Skeleton block matching the real component's dimensions. */
export function PowerSkeleton({
  className = ''
}: {
  className?: string;
}) {
  return <div className={`cz-skeleton rounded-xl ${className}`} aria-hidden data-id="element-4344" data-mp="3a9p8bph59csi" />;
}